import { useEffect, useState } from "react";
import { api } from "../lib/api";

// Lightweight markdown: line-by-line with # / ## / - / blank-line paragraphs + **bold** + *italic*
function renderInline(text) {
  // Split on **bold** and *italic* preserving order
  const tokens = [];
  let remaining = text;
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/;
  while (remaining.length > 0) {
    const m = remaining.match(re);
    if (!m) {
      tokens.push(remaining);
      break;
    }
    if (m.index > 0) tokens.push(remaining.slice(0, m.index));
    if (m[1]) tokens.push({ bold: m[1] });
    else if (m[2]) tokens.push({ italic: m[2] });
    remaining = remaining.slice(m.index + m[0].length);
  }
  return tokens.map((t, i) => {
    if (typeof t === "string") return <span key={i}>{t}</span>;
    if (t.bold) return <strong key={i} className="text-white font-semibold">{t.bold}</strong>;
    if (t.italic) return <em key={i}>{t.italic}</em>;
    return null;
  });
}

function Markdown({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let currentPara = [];
  let currentList = null;

  function flushPara() {
    if (currentPara.length) {
      blocks.push({ type: "p", text: currentPara.join(" ") });
      currentPara = [];
    }
  }
  function flushList() {
    if (currentList) {
      blocks.push({ type: "ul", items: currentList });
      currentList = null;
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith("# ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h1", text: line.slice(2).trim() });
    } else if (line.startsWith("## ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3).trim() });
    } else if (line.startsWith("- ")) {
      flushPara();
      if (!currentList) currentList = [];
      currentList.push(line.slice(2).trim());
    } else {
      flushList();
      currentPara.push(line.trim());
    }
  }
  flushPara();
  flushList();

  return (
    <div className="space-y-5 text-neutral-300 leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === "h1")
          return (
            <h2 key={i} className="font-display font-black text-3xl sm:text-4xl text-white pt-2">
              {renderInline(b.text)}
            </h2>
          );
        if (b.type === "h2")
          return (
            <h3 key={i} className="font-display font-bold text-xl sm:text-2xl text-axe-tangerine pt-4">
              {renderInline(b.text)}
            </h3>
          );
        if (b.type === "ul")
          return (
            <ul key={i} className="list-disc pl-6 space-y-2 marker:text-axe-purple">
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ul>
          );
        return <p key={i}>{renderInline(b.text)}</p>;
      })}
    </div>
  );
}

export default function LegalPage({ kind }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/site-content").then((r) => setC(r.data)).catch(() => {});
  }, []);

  const title = kind === "terms" ? "Terms of Service" : "Privacy Policy";
  const body = c ? (kind === "terms" ? c.terms_content : c.privacy_content) : "";

  return (
    <div data-testid={`${kind}-page`} className="text-white">
      <section className="max-w-3xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20">
        <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
          Legal
        </div>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl">{title}</h1>
        <div className="mt-10">
          {c ? <Markdown text={body} /> : <div className="text-neutral-400">Loading…</div>}
        </div>
      </section>
    </div>
  );
}
